import React, { FC, createContext, ReactNode, useMemo, useEffect } from 'react'
import ReactGA from 'react-ga4'
import { datadogRum } from '@datadog/browser-rum'

interface AnalyticsProviderProps {
  children: ReactNode
  trackEssential?: boolean
  trackPerformance?: boolean
  trackFunctional?: boolean
  trackTargeting?: boolean
}

const AnalyticsContext = createContext({
  trackEssential: true,
  trackPerformance: false,
  trackFunctional: false,
  trackTargeting: false,
})

export const AnalyticsProvider: FC<AnalyticsProviderProps> = ({
  children,
  trackEssential = true,
  trackFunctional = false,
  trackPerformance = false,
  trackTargeting = false,
}) => {
  // Declare instances
  const datadog = datadogRum
  const ga4 = ReactGA

  // Determine whether instances should run
  const shouldRunDatadog = trackPerformance && DATADOG_APP_ID !== '' // && RELEASE_STAGE !== 'dev'
  const shouldRunGA = trackPerformance && GA_MEASUREMENT_ID !== ''

  const val = useMemo(
    () => ({ trackEssential, trackFunctional, trackPerformance, trackTargeting }),
    [trackEssential, trackFunctional, trackPerformance, trackTargeting]
  )

  // Trigger datadog insatnce
  useEffect(() => {
    if (shouldRunDatadog) {
      datadog.init({
        applicationId: DATADOG_APP_ID,
        clientToken: DATADOG_CLIENT_TOKEN,
        env: RELEASE_STAGE,
        site: 'datadoghq.eu',
        service: DATADOG_SERVICE,
        version: BUILD_VERSION,
        sampleRate: 100,
        sessionReplaySampleRate: 20,
        trackInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
      })
      datadog.startSessionReplayRecording()
    } else {
      datadog.stopSession()
      datadog.stopSessionReplayRecording()
    }
  }, [datadog, shouldRunDatadog])

  // Trigger GA instance
  useEffect(() => {
    if (shouldRunGA) {
      ga4.initialize(GA_MEASUREMENT_ID)
    } else {
      ga4.initialize('DISABLED')
    }
  }, [ga4, shouldRunGA])

  return <AnalyticsContext.Provider value={val}>{children}</AnalyticsContext.Provider>
}

export default AnalyticsContext
